import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import AlertComponent from "../../components/AlertComponent";
import Modal from "../../components/Modal";
import { EmptyState, PageHeader, Panel } from "../../components/Page";
import StatusBadge from "../../components/StatusBadge";
import { apiUrl, getPayload } from "../../services/apiConfig";
import { errorMessage } from "../../utils/formatters";

const emptyGroup = {
  nome: "",
  descricao: "",
  emails: [""],
};

function fromGroup(group) {
  if (!group) return emptyGroup;

  return {
    nome: group.nome ?? "",
    descricao: group.descricao ?? "",
    emails: group.emails?.length ? group.emails : [""],
  };
}

function GrupoComunicacaoErroForm({ initialGroup, onCancel, onSave }) {
  const [form, setForm] = useState(() => fromGroup(initialGroup));

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setEmail = (index, value) => {
    setForm((current) => ({
      ...current,
      emails: current.emails.map((email, idx) => (idx === index ? value : email)),
    }));
  };

  const addEmail = () => {
    setForm((current) => ({ ...current, emails: [...current.emails, ""] }));
  };

  const removeEmail = (index) => {
    setForm((current) => ({
      ...current,
      emails: current.emails.length === 1 ? [""] : current.emails.filter((_, idx) => idx !== index),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({
      nome: form.nome,
      descricao: form.descricao || null,
      emails: form.emails.map((email) => email.trim()).filter(Boolean),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Nome</label>
        <input value={form.nome} onChange={(event) => setField("nome", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-slate-700">Descricao</label>
        <input value={form.descricao} onChange={(event) => setField("descricao", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" />
      </div>

      <div className="rounded-lg border border-slate-200">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Destinatarios</h3>
            <p className="mt-1 text-xs text-slate-500">Informe os e-mails que receberao avisos quando um job falhar.</p>
          </div>
          <button type="button" onClick={addEmail} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
            Adicionar
          </button>
        </div>

        <div className="space-y-3 p-4">
          {form.emails.map((email, index) => (
            <div key={index} className="grid gap-3 sm:grid-cols-[1fr_40px]">
              <input type="email" value={email} onChange={(event) => setEmail(index, event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" placeholder="nome@empresa.com.br" required />
              <button type="button" title="Remover e-mail" onClick={() => removeEmail(index)} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-red-600 transition hover:bg-red-50 hover:text-red-700">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          Cancelar
        </button>
        <button type="submit" className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
          Salvar grupo
        </button>
      </div>
    </form>
  );
}

export default function GruposComunicacaoErroPage() {
  const [grupos, setGrupos] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingGroup, setEditingGroup] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = () => {
    setErro("");
    axios
      .get(apiUrl("/grupos-comunicacao-erro"))
      .then((response) => setGrupos(getPayload(response) || []))
      .catch((error) => setErro(errorMessage(error, "Nao foi possivel carregar os grupos.")));
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return grupos;

    return grupos.filter((grupo) =>
      [grupo.nome, grupo.descricao, ...(grupo.emails ?? [])]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [filter, grupos]);

  const openCreate = () => {
    setEditingGroup(null);
    setShowForm(true);
  };

  const openEdit = (grupo) => {
    setEditingGroup(grupo);
    setShowForm(true);
  };

  const saveGroup = (payload) => {
    const request = editingGroup
      ? axios.put(apiUrl(`/grupos-comunicacao-erro/${editingGroup.id}`), payload)
      : axios.post(apiUrl("/grupos-comunicacao-erro"), payload);

    request
      .then(() => {
        AlertComponent("Pronto", editingGroup ? "Grupo atualizado." : "Grupo cadastrado.");
        setShowForm(false);
        setEditingGroup(null);
        carregar();
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Nao foi possivel salvar o grupo."), { icon: "error" }));
  };

  const toggleStatus = (grupo) => {
    axios
      .patch(apiUrl(`/grupos-comunicacao-erro/${grupo.id}/status`), { ativo: !grupo.ativo })
      .then(() => carregar())
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Nao foi possivel alterar o status."), { icon: "error" }));
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Grupos de falha"
        subtitle="Listas de e-mail usadas para comunicar falhas na execucao dos jobs."
        actions={
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
            Novo grupo
          </button>
        }
      />

      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

      <Panel
        actions={
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Buscar grupo" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 sm:w-80" />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Grupo</th>
                <th className="px-4 py-3">Destinatarios</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((grupo) => (
                <tr key={grupo.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{grupo.nome}</p>
                    <p className="max-w-md truncate text-xs text-slate-500">{grupo.descricao || "-"}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(grupo.emails ?? []).map((email) => (
                        <span key={email} className="rounded-full border border-violet-100 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">{email}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge value={grupo.ativo ? "Ativo" : "Inativo"} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" title="Editar" onClick={() => openEdit(grupo)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m16.86 4.49 2.65 2.65M5 19l4.5-1 9.9-9.9a1.88 1.88 0 0 0-2.65-2.65l-9.9 9.9L5 19Z" />
                        </svg>
                      </button>
                      <button type="button" title={grupo.ativo ? "Inativar" : "Ativar"} onClick={() => toggleStatus(grupo)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v9m6.36-6.36a9 9 0 1 1-12.72 0" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <EmptyState title="Nenhum grupo encontrado." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {showForm && (
        <Modal title={editingGroup ? "Editar grupo" : "Novo grupo"} subtitle="Cadastre os e-mails que serao comunicados quando um job falhar." onClose={() => setShowForm(false)} width="max-w-3xl">
          <GrupoComunicacaoErroForm initialGroup={editingGroup} onCancel={() => setShowForm(false)} onSave={saveGroup} />
        </Modal>
      )}
    </div>
  );
}
